import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useOptionalAccountWorkspace } from "@/lib/account-workspace";
import { useAuth, type AuthUser } from "@/lib/auth";
import type { HermesDbSkill } from "@/lib/hermes-api";

type Membership = {
  user_id: number;
  role: "owner" | "admin" | "member";
  disabled: boolean;
};

export type HermesAccountSkillsPermission = {
  canManage: boolean;
  ready: boolean;
};

/** 根据 membership 判定是否可管理企业技能（纯函数，便于单测） */
export function canManageAccountSkillsFromMembership(
  membership: Membership | undefined | null,
): boolean {
  return Boolean(
    membership &&
      !membership.disabled &&
      (membership.role === "owner" || membership.role === "admin"),
  );
}

/** 账户 admin/owner：可安装/编辑/删除企业技能 */
export function useHermesCanManageAccountSkills(accountId: string): HermesAccountSkillsPermission {
  const workspace = useOptionalAccountWorkspace();
  const accessKind = workspace?.accessKind ?? null;
  const workspaceAccountId = workspace?.accountId ?? "";
  const { user } = useAuth();
  const [canManage, setCanManage] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!accountId || !user?.id) {
      setCanManage(false);
      setReady(true);
      return;
    }

    // 明确为非成员授权进入时，无需请求 memberships
    if (workspaceAccountId === accountId && accessKind !== "membership") {
      setCanManage(false);
      setReady(true);
      return;
    }

    let cancelled = false;
    setReady(false);
    setCanManage(false);

    api
      .fetchForAccount<{ memberships: Membership[] }>(accountId, `/accounts/${accountId}/memberships`)
      .then(({ memberships }) => {
        if (cancelled) return;
        const membership = memberships.find((item) => item.user_id === user.id);
        setCanManage(canManageAccountSkillsFromMembership(membership));
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setCanManage(false);
          setReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, accessKind, user?.id, workspaceAccountId]);

  return { canManage, ready };
}

/** 平台运营账号（可编辑系统技能、改系统启用状态）。 */
export function isPlatformStaff(user: AuthUser | null | undefined): boolean {
  return Boolean(user?.platform_role);
}

export function canEditHermesSkill(
  skill: HermesDbSkill,
  options: { isPlatformStaff: boolean; canManageAccount: boolean },
): boolean {
  if (skill.visibility === "system") return options.isPlatformStaff;
  if (skill.visibility === "account") return options.canManageAccount;
  return true;
}

export function canDeleteHermesSkill(
  skill: HermesDbSkill,
  options: { isPlatformStaff: boolean; canManageAccount: boolean },
): boolean {
  if (skill.visibility === "system") return false;
  if (skill.visibility === "account") return options.canManageAccount;
  return true;
}

export function canToggleHermesSkillEnabled(
  skill: HermesDbSkill,
  options: { isPlatformStaff: boolean; canManageAccount: boolean },
): boolean {
  if (skill.visibility === "system") return options.isPlatformStaff;
  if (skill.visibility === "account") return options.canManageAccount;
  return true;
}
